import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, X, Sparkles, Shield } from 'lucide-react';
import { liteApi, litePro } from '@/utils/lite/liteApi';

/**
 * LiteProCheckoutModal — collects email, kicks off a Paystack checkout for
 * Lite Pro. After Paystack success, the user is redirected to
 * `/us/billing/success?reference=…` which verifies and stores the unlock
 * token. No PlayFirst account is created.
 */
export default function LiteProCheckoutModal({ open, onClose, billingPeriod = 'monthly' }) {
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem(litePro.EMAIL_KEY) || ''; } catch { return ''; }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const price = billingPeriod === 'annual' ? 89.99 : 9.99;
  const period = billingPeriod === 'annual' ? '/year' : '/month';

  const handleCheckout = async () => {
    setError('');
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email — this is how we recognize your subscription.');
      return;
    }
    // Persist email so the success page can recover it if needed
    try { localStorage.setItem(litePro.EMAIL_KEY, email.toLowerCase().trim()); } catch {/* ignore */}
    setLoading(true);
    try {
      const res = await liteApi.checkoutLitePro({
        email: email.toLowerCase().trim(),
        billing_period: billingPeriod,
        currency: 'ZAR', // Paystack test-mode default; adjust if you enable USD on your merchant account
      });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        setError('Could not start checkout. Please try again.');
      }
    } catch (e) {
      setError(e.message || 'Could not start checkout.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
      data-testid="lite-pro-checkout-modal"
      onClick={() => !loading && onClose?.()}
    >
      <div
        className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="font-serif text-lg text-primary-900">Unlock unlimited Lite</h3>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="lite-pro-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-2xl bg-emerald-50/60 border border-emerald-200 p-4">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-primary-900">${price}</span>
              <span className="text-stone-500 text-sm">{period}</span>
            </div>
            {billingPeriod === 'annual' && (
              <p className="text-xs text-emerald-800 mt-1 font-medium">Save $30/yr · 25% off vs monthly</p>
            )}
            <p className="text-sm text-stone-700 mt-3 leading-relaxed">
              Unlimited AI generations across all Lite tools — Report Builder, Parent Summary, GLP Guide, Activity Generator. No data stored on our servers.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">Your email</Label>
            <Input
              type="email"
              placeholder="you@clinic.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="lite-pro-email-input"
              disabled={loading}
            />
            <p className="text-xs text-stone-500 flex items-start gap-1.5 mt-2">
              <Shield className="w-3 h-3 mt-0.5 flex-shrink-0 text-emerald-700" />
              Identity = email. We send a magic link after payment so you can unlock from any device. No full account, no PHI stored.
            </p>
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="lite-pro-checkout-error">
              {error}
            </div>
          )}

          <Button
            onClick={handleCheckout}
            disabled={loading}
            className="w-full rounded-full"
            data-testid="lite-pro-checkout-btn"
          >
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting checkout…</> : <>Continue to Paystack →</>}
          </Button>

          <p className="text-xs text-stone-400 text-center">
            Powered by Paystack · Cancel anytime · Stateless billing
          </p>
        </div>
      </div>
    </div>
  );
}
